import * as React from 'react';
import Helmet from 'react-helmet';
import { Grid, Skeleton, Typography } from '@mui/material';
import { useParams } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useGetGenreEventsQuery, useLazyGetEventsByGenreQuery } from '../../core/api/eventsSlice';
import { EventsGrid } from '../../components/eventsV2/eventsGrid';
import { useUtils } from '../../services/useUtils';

const useStyles = makeStyles()(() => ({
  content: {
    marginTop: '120px',
    padding: '0 16px 60px 16px',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: '12px',
    rowGap: '4px',
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    lineHeight: '32px',
    margin: 0,
  },
  eventsCount: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 14,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
  },
}));

const PAGE_SIZE = 12;

export const CategoryEvents = () => {
  const { genreSlug } = useParams<CategoryEventsUrlParams>();
  const { classes } = useStyles();
  const { toPageTitle } = useUtils();
  const { t } = useTranslation();

  const { data: genre, isLoading: isLoadingGenre } = useGetGenreEventsQuery(genreSlug);
  const [getEventsByGenreRequest] = useLazyGetEventsByGenreQuery();

  const loadEvents = async (skip = 0) => {
    if (!genre) return [];
    const { data } = await getEventsByGenreRequest({ genreId: genre.id, skip, count: PAGE_SIZE });
    return data ?? [];
  };

  return (
    <Grid container direction="row" justifyContent="center" className={classes.content}>
      <Helmet>
        <title>{toPageTitle(genre?.title ?? '')}</title>
      </Helmet>

      <Grid item xs={12}>
        {isLoadingGenre ? (
          <Skeleton variant="rectangular" width={220} height={32} />
        ) : (
          <div className={classes.header}>
            <Typography variant="h4" component="h1" className={classes.title}>
              {genre?.title}
            </Typography>
            <Typography component="span" className={classes.eventsCount}>
              {t('Genre.EventsCount', { count: genre?.eventsCount ?? 0 })}
            </Typography>
          </div>
        )}
      </Grid>

      <Grid item xs={12}>
        {!isLoadingGenre && genre && (
          <EventsGrid reloadDependencyValue={genre.id} load={loadEvents} pageSize={PAGE_SIZE} itemXs={4} compact />
        )}
      </Grid>
    </Grid>
  );
};

type CategoryEventsUrlParams = {
  genreSlug: string;
};
